/**
 * useToolsState — Encapsulates state and handlers for the 4 financial tools:
 * Test, Savings Index, Goal Calculator, and Growth Calculator.
 *
 * Returns a flat bag of state + setters + actions so the parent page can
 * forward it to ToolsSection without managing individual hooks.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { analytics } from '../services/analytics';
import { mockTestResult } from '../data/mockData';

export const useToolsState = ({ challenge18, setChallenge18 }) => {
  // ---- Financial Test ----
  const [testAnswers, setTestAnswers] = useState({ q1: '', q2: '' });
  const [testResult, setTestResult] = useState(null);

  const handleTest = () => {
    if (!testAnswers.q1 || !testAnswers.q2) {
      toast.error('Por favor responde todas las preguntas');
      return;
    }
    const score = parseInt(testAnswers.q1) + parseInt(testAnswers.q2);
    const result = mockTestResult(score);
    setTestResult(result);
    analytics.completeTest(result.profile, score);

    // 🥚 Easter egg: worst-case combo ("se evapora en 72 horas" + "0% no ahorro")
    if (testAnswers.q1 === '1' && testAnswers.q2 === '1') {
      setTimeout(() => {
        toast('🚨 Detectado: especialista en arrepentirse los domingos 😔', {
          description: 'Te urge el botón verde de allá abajo. Llévanos al dolor con calma — automaticemos esto antes del próximo viernes.',
          duration: 9000,
          className: 'toast-easter-egg'
        });
        // Shake the main email CTA button to call attention
        const ctaBtn = document.querySelector('[data-testid="email-capture-submit"]');
        if (ctaBtn) {
          ctaBtn.classList.remove('cta-shake');
          // Force reflow so the class re-applies and animation restarts
          void ctaBtn.offsetWidth;
          ctaBtn.classList.add('cta-shake');
          setTimeout(() => ctaBtn.classList.remove('cta-shake'), 1600);
        }
        analytics.ctaClick('easter_egg_triggered');
      }, 600);
    }
  };

  // ---- Savings Index ----
  const [indiceData, setIndiceData] = useState({ income: '', auto: '', percentage: 0 });

  const calculateIndice = () => {
    if (!indiceData.income || !indiceData.auto) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    const percentage = (parseFloat(indiceData.auto) / parseFloat(indiceData.income)) * 100;
    setIndiceData({ ...indiceData, percentage: percentage.toFixed(1) });
    analytics.savingsIndexCalculated(parseFloat(percentage.toFixed(1)));
  };

  const applyIndiceToChallenge18 = () => {
    const monthlyAmount = parseFloat(indiceData.auto);
    if (!monthlyAmount || monthlyAmount <= 0) return;
    setChallenge18((prev) => ({
      ...prev,
      monthlyAmount: String(monthlyAmount),
      totalSavings: 0,
      withInterest: 0
    }));
    analytics.ctaClick('indice_to_challenge18');
    setTimeout(() => {
      document.getElementById('reto-18-meses')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    toast.success(`💪 Aplicado al Reto 18 meses: $${monthlyAmount.toLocaleString('es-MX')}/mes`);
  };

  // ---- Goal Calculator ----
  const [goalData, setGoalData] = useState({
    goalName: '',
    goalAmount: '',
    currentSavings: '',
    monthlyContribution: '',
    rate: '8',
    monthsNeeded: null,
    yearsNeeded: null
  });

  const selectGoalPreset = (preset) => {
    setGoalData((prev) => ({
      ...prev,
      goalName: preset.name,
      goalAmount: String(preset.amount),
      monthlyContribution: String(preset.monthlyAmount),
      rate: prev.rate || '8',
      selectedPreset: preset.id,
      yearsNeeded: null,
      monthsNeeded: 0
    }));
  };

  const calculateGoal = () => {
    if (!goalData.goalAmount || !goalData.currentSavings || !goalData.monthlyContribution) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    const goal = parseFloat(goalData.goalAmount);
    const current = parseFloat(goalData.currentSavings);
    const monthly = parseFloat(goalData.monthlyContribution);
    const rate = parseFloat(goalData.rate) / 100 / 12;
    const remaining = goal - current;

    if (monthly <= 0) {
      toast.error('El aporte mensual debe ser mayor a 0');
      return;
    }

    const months = rate === 0
      ? remaining / monthly
      : Math.log(1 + (remaining * rate) / monthly) / Math.log(1 + rate);
    const years = (months / 12).toFixed(1);

    setGoalData({
      ...goalData,
      monthsNeeded: Math.ceil(months),
      yearsNeeded: years
    });
    analytics.goalCalculated(goalData.goalName || 'custom', goal, parseFloat(years));
    toast.success('¡Meta calculada! Revisa los resultados abajo');
  };

  // ---- Growth Calculator ----
  const [calcData, setCalcData] = useState({ amount: '', years: '', rate: '' });
  const [chartData, setChartData] = useState([]);
  const [futureValue, setFutureValue] = useState(null);

  const calculateGrowth = () => {
    if (!calcData.amount || !calcData.years || !calcData.rate) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    const amount = parseFloat(calcData.amount);
    const years = parseInt(calcData.years);
    const rate = parseFloat(calcData.rate) / 100;
    const months = years * 12;
    const monthlyRate = rate / 12;

    const futureVal = amount * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    setFutureValue(futureVal);

    const data = [];
    const step = months <= 60 ? 1 : (months <= 180 ? 3 : 6);
    for (let i = 0; i <= months; i += step) {
      const balance = i === 0
        ? 0
        : amount * ((Math.pow(1 + monthlyRate, i) - 1) / monthlyRate) * (1 + monthlyRate);
      data.push({ month: i, value: balance });
    }
    if (data[data.length - 1].month !== months) {
      data.push({ month: months, value: futureVal });
    }
    setChartData(data);
  };

  return {
    // Test
    testAnswers, setTestAnswers, testResult, handleTest,
    // Index
    indiceData, setIndiceData, calculateIndice, applyIndiceToChallenge18,
    // Goal
    goalData, setGoalData, selectGoalPreset, calculateGoal,
    // Growth
    calcData, setCalcData, chartData, futureValue, calculateGrowth
  };
};
